import { ItiLink as Link } from 'components';
import { BookingNumber, StackUnitNumber } from 'components/tables/utils';
import dayjs from 'dayjs';

import { Role, TransactionRow } from 'types';
import { T_DataTableColumn } from 'types/data-table';

interface TransactionColumnType {
  id: T_DataTableColumn<TransactionRow, 'number'>;
  type: T_DataTableColumn<TransactionRow, 'text'>;
  inbound: T_DataTableColumn<TransactionRow, 'boolean'>;
  damages: T_DataTableColumn<TransactionRow, 'damages'>;
  location: T_DataTableColumn<TransactionRow, 'location'>;
  truckingCompany: T_DataTableColumn<TransactionRow, 'truckingCompany'>;
  booking: T_DataTableColumn<TransactionRow, 'booking'>;
  customerName: T_DataTableColumn<TransactionRow, 'customer'>;
  customerFullName: T_DataTableColumn<TransactionRow, 'customer'>;
  customerCode: T_DataTableColumn<TransactionRow, 'customer'>;
  customerLocation: T_DataTableColumn<TransactionRow, 'customer'>;
  containerCustomerName: T_DataTableColumn<TransactionRow, 'customer'>;
  containerCustomerFullName: T_DataTableColumn<TransactionRow, 'customer'>;
  containerCustomerCode: T_DataTableColumn<TransactionRow, 'customer'>;
  containerCustomerLocation: T_DataTableColumn<TransactionRow, 'customer'>;
  chassisCustomerName: T_DataTableColumn<TransactionRow, 'customer'>;
  chassisCustomerFullName: T_DataTableColumn<TransactionRow, 'customer'>;
  chassisCustomerCode: T_DataTableColumn<TransactionRow, 'customer'>;
  chassisCustomerLocation: T_DataTableColumn<TransactionRow, 'customer'>;
  gensetCustomerName: T_DataTableColumn<TransactionRow, 'customer'>;
  gensetCustomerFullName: T_DataTableColumn<TransactionRow, 'customer'>;
  gensetCustomerCode: T_DataTableColumn<TransactionRow, 'customer'>;
  gensetCustomerLocation: T_DataTableColumn<TransactionRow, 'customer'>;
  sizeType: T_DataTableColumn<TransactionRow, 'sizeType'>;
  unitStatus: T_DataTableColumn<TransactionRow, 'unitStatus'>;
  statusReason: T_DataTableColumn<TransactionRow, 'text'>;
  containerId: T_DataTableColumn<TransactionRow, 'number'>;
  container: T_DataTableColumn<TransactionRow, 'unit'>;
  containerNumber: T_DataTableColumn<TransactionRow, 'text'>;
  chassisId: T_DataTableColumn<TransactionRow, 'number'>;
  chassis: T_DataTableColumn<TransactionRow, 'stack'>;
  chassisAccount: T_DataTableColumn<TransactionRow, 'text'>;
  chassisNumber: T_DataTableColumn<TransactionRow, 'text'>;
  chassisSizeType: T_DataTableColumn<TransactionRow, 'sizeType'>;
  chassisStatus: T_DataTableColumn<TransactionRow, 'unitStatus'>;
  chassisStatusReason: T_DataTableColumn<TransactionRow, 'text'>;
  gensetId: T_DataTableColumn<TransactionRow, 'number'>;
  gensets: T_DataTableColumn<TransactionRow, 'stack'>;
  gensetAccount: T_DataTableColumn<TransactionRow, 'text'>;
  gensetNumber: T_DataTableColumn<TransactionRow, 'text'>;
  gensetSizeType: T_DataTableColumn<TransactionRow, 'sizeType'>;
  gensetStatus: T_DataTableColumn<TransactionRow, 'unitStatus'>;
  gensetStatusReason: T_DataTableColumn<TransactionRow, 'text'>;
  notes: T_DataTableColumn<TransactionRow, 'text'>;
  driverName: T_DataTableColumn<TransactionRow, 'text'>;
  cdlNumber: T_DataTableColumn<TransactionRow, 'text'>;
  licensePlate: T_DataTableColumn<TransactionRow, 'text'>;
  truckNumber: T_DataTableColumn<TransactionRow, 'text'>;
  email: T_DataTableColumn<TransactionRow, 'text'>;
  username: T_DataTableColumn<TransactionRow, 'text'>;
  completedDate: T_DataTableColumn<TransactionRow, 'dateTime'>;
  chassisLicensePlate: T_DataTableColumn<TransactionRow, 'text'>;
  flatbed: T_DataTableColumn<TransactionRow, 'boolean'>;
  ownerChassis: T_DataTableColumn<TransactionRow, 'boolean'>;
  fmcsaDate: T_DataTableColumn<TransactionRow, 'date'>;
  licensePlateExpirationDate: T_DataTableColumn<TransactionRow, 'date'>;
  hazard: T_DataTableColumn<TransactionRow, 'boolean'>;
  loaded: T_DataTableColumn<TransactionRow, 'boolean'>;
  weight: T_DataTableColumn<TransactionRow, 'number'>;
  humidity: T_DataTableColumn<TransactionRow, 'number'>;
  temperature: T_DataTableColumn<TransactionRow, 'number'>;
  ventilation: T_DataTableColumn<TransactionRow, 'text'>;
  carbCompliant: T_DataTableColumn<TransactionRow, 'boolean'>;
  fuelLevel: T_DataTableColumn<TransactionRow, 'number'>;
}

type TransactionColumn = { [P in keyof TransactionColumnType]: TransactionColumnType[P] }[keyof TransactionColumnType];

export const transactionDataColumns: TransactionColumn[] = [
  /* general */
  {
    width: 112,
    field: 'id',
    label: 'RECORD',
    type: 'number',
    category: 'common',
    renderCell: id => <Link to={`/transactions/${id}`}>{id}</Link>
  },
  {
    width: 144,
    field: 'completedDate',
    type: 'dateTime',
    label: 'DATE',
    category: 'common',
    dateFormat: 'l LT',
    className: 'backdated',
    classCondition: (completedDate, row) => !dayjs(completedDate).isSame(dayjs(row?.createdDate), 'hour')
  },
  {
    width: 100,
    field: 'username',
    label: 'USER',
    type: 'text',
    category: 'common',
    getValueLabel: (username, row) => username ?? row.email?.replace(/@.+/, ''),
    allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING]
  },
  {
    width: 80,
    field: 'inbound',
    type: 'boolean',
    label: 'INBOUND',
    category: 'common',
    skipQuickSearch: true
  },
  {
    width: 80,
    field: 'bulk',
    type: 'boolean',
    label: 'BULK',
    category: 'common',
    skipQuickSearch: true
  },
  {
    width: 80,
    field: 'type',
    type: 'text',
    label: 'TYPE',
    category: 'common',
    getValueLabel: (type, row) => type || `${row.bulk ? 'BULK' : 'GATE'} ${row.inbound ? 'IN' : 'OUT'}`,
    skipQuickSearch: true,
    filterable: false
  },
  {
    width: 160,
    field: 'location',
    label: 'LOCATION',
    type: 'location',
    category: 'common',
    getValueLabel: location => location?.name
  },
  {
    width: 144,
    field: 'booking',
    label: 'BOOKING',
    type: 'booking',
    category: 'common',
    getValueLabel: booking => booking?.bookingNumber,
    renderCell: booking => <BookingNumber booking={booking} ADMIN BILLING_ADMIN BOOKING CUSTOMER />
  },
  // TODO: unimplemented?
  {
    width: 144,
    field: 'equipmentDeliveryOrder',
    label: 'EQ ORDER',
    category: 'common',
    sortable: false,
    filterable: false,
    skipQuickSearch: true
  },
  // TODO: unimplemented?
  {
    width: 144,
    field: 'redeliveryNumber',
    label: 'REDELIVERY',
    category: 'common',
    sortable: false,
    filterable: false,
    skipQuickSearch: true
  },
  {
    width: 80,
    field: 'containerId',
    label: 'CONT ID',
    category: 'container',
    type: 'number'
  },
  {
    width: 180,
    field: 'container',
    label: 'CONTAINER',
    category: 'container',
    type: 'unit',
    getValueLabel: (_, row) => row.containerNumber,
    renderCell: (container, row) => (
      <StackUnitNumber
        stack={!container ? null : [container]}
        unitNumber={row.containerNumber}
        ADMIN
        BILLING_ADMIN
        ADMIN_CUSTOMER
        BOOKING
        CUSTOMER
        EXTERNAL
      />
    )
  },
  {
    width: 80,
    field: 'chassisId',
    label: 'CHAS ID',
    category: 'chassis',
    type: 'number'
  },
  {
    width: 180,
    field: 'chassis',
    label: 'CHASSIS',
    category: 'chassis',
    type: 'stack',
    getValueLabel: (value, row) =>
      value && value.length > 1 ? `${row.chassisNumber} (+${value.length - 1})` : row.chassisNumber,
    renderCell: (chasses, row) => (
      <StackUnitNumber
        flatbed={row.flatbed}
        ownerChassis={row.ownerChassis}
        stack={chasses}
        unitNumber={row.chassisNumber}
        ADMIN
        BILLING_ADMIN
        ADMIN_CUSTOMER
        BOOKING
        CUSTOMER
        EXTERNAL
      />
    )
  },
  {
    width: 80,
    field: 'gensetId',
    label: 'GENS ID',
    category: 'genset',
    type: 'number'
  },
  {
    width: 180,
    field: 'gensets',
    label: 'GENSET',
    category: 'genset',
    type: 'stack',
    getValueLabel: (value, row) =>
      value && value.length > 1 ? `${row.gensetNumber} (+${value.length - 1})` : row.gensetNumber,
    renderCell: (gensets, row) => (
      <StackUnitNumber stack={gensets} unitNumber={row.gensetNumber} ADMIN BILLING_ADMIN BOOKING CUSTOMER EXTERNAL />
    )
  },
  /* size types */
  { width: 60, field: 'sizeType', label: 'SIZE TYPE', category: 'container' },
  {
    width: 144,
    field: 'chassisSizeType',
    label: 'CHAS SIZE TYPE',
    category: 'chassis'
  },
  {
    width: 144,
    field: 'gensetSizeType',
    label: 'GENS SIZE TYPE',
    category: 'genset'
  },
  /* customers */
  { width: 144, field: 'customerName', label: 'ACCOUNT', type: 'customer', category: 'common' },
  {
    width: 144,
    field: 'customerFullName',
    label: 'FULL ACCT',
    category: 'common',
    filterable: false,
    skipQuickSearch: true,
    type: 'customer'
  },
  { width: 144, field: 'customerCode', label: 'ACCT CODE', type: 'customer', category: 'common' },
  { width: 144, field: 'customerLocation', label: 'ACCT LOC', type: 'customer', category: 'common' },
  { width: 144, field: 'containerCustomerName', label: 'CONT ACCT', type: 'customer', category: 'container' },
  {
    width: 144,
    field: 'containerCustomerFullName',
    label: 'CONT FULL ACCT',
    category: 'container',
    filterable: false,
    skipQuickSearch: true,
    type: 'customer'
  },
  { width: 144, field: 'containerCustomerCode', label: 'CONT ACCT CODE', type: 'customer', category: 'container' },
  { width: 144, field: 'containerCustomerLocation', label: 'CONT ACCT LOC', type: 'customer', category: 'container' },
  { width: 144, field: 'chassisCustomerName', label: 'CHAS ACCT', type: 'customer', category: 'chassis' },
  {
    width: 144,
    field: 'chassisCustomerFullName',
    label: 'CHAS FULL ACCT',
    category: 'chassis',
    filterable: false,
    skipQuickSearch: true,
    type: 'customer'
  },
  { width: 144, field: 'chassisCustomerCode', label: 'CHAS ACCT CODE', type: 'customer', category: 'chassis' },
  { width: 144, field: 'chassisCustomerLocation', label: 'CHAS ACCT LOC', type: 'customer', category: 'chassis' },
  { width: 144, field: 'gensetCustomerName', label: 'GEN ACCT', type: 'customer', category: 'genset' },
  {
    width: 144,
    field: 'gensetCustomerFullName',
    label: 'GEN FULL ACCT',
    category: 'genset',
    filterable: false,
    skipQuickSearch: true,
    type: 'customer'
  },
  { width: 144, field: 'gensetCustomerCode', label: 'GEN ACCT CODE', type: 'customer', category: 'genset' },
  { width: 144, field: 'gensetCustomerLocation', label: 'GEN ACCT LOC', type: 'customer', category: 'genset' },
  /* statuses */
  {
    width: 128,
    field: 'unitStatus',
    label: 'STATUS',
    category: 'container'
  },
  {
    width: 144,
    field: 'statusReason',
    label: 'STATUS NOTES',
    category: 'container'
  },
  {
    width: 144,
    field: 'chassisStatus',
    label: 'CHAS STATUS',
    category: 'chassis'
  },
  {
    width: 144,
    field: 'chassisStatusReason',
    label: 'CHAS STATUS NOTES',
    category: 'chassis'
  },
  {
    width: 144,
    field: 'gensetStatus',
    label: 'GENS STATUS',
    category: 'genset'
  },
  {
    width: 144,
    field: 'gensetStatusReason',
    label: 'GENS STATUS NOTES',
    category: 'genset'
  },
  /* trucking */
  {
    width: 80,
    field: 'scacCode',
    label: 'SCAC',
    category: 'common',
    type: 'text'
  },
  {
    width: 160,
    field: 'truckingCompany',
    label: 'TRUCK COMPANY',
    category: 'common',
    type: 'truckingCompany',
    getValueLabel: truckingCompany => truckingCompany?.name ?? ''
  },
  { width: 144, field: 'driverName', label: 'DRIVER', category: 'common' },
  { width: 128, field: 'cdlNumber', label: 'CDL', category: 'common' },
  { width: 128, field: 'licensePlate', label: 'TRUCK LP', category: 'common' },
  { width: 128, field: 'truckNumber', label: 'TRUCK', category: 'common' },
  /* misc. equipment */
  {
    width: 144,
    field: 'hazard',
    type: 'boolean',
    label: 'HAZARDS',
    category: 'container',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'loaded',
    type: 'boolean',
    label: 'LOADED',
    category: 'container',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'weight',
    type: 'number',
    label: 'WEIGHT',
    category: 'container',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'humidity',
    type: 'number',
    label: 'HUMIDITY',
    category: 'genset',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'temperature',
    type: 'number',
    label: 'TEMP',
    category: 'genset',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'ventilation',
    type: 'number',
    label: 'VENT',
    category: 'genset',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'fuelLevel',
    type: 'number',
    label: 'FUEL',
    category: 'genset',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'carbCompliant',
    type: 'boolean',
    label: 'CARB',
    category: 'genset',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'flatbed',
    type: 'boolean',
    label: 'FLATBED',
    category: 'chassis',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'ownerChassis',
    type: 'boolean',
    label: 'OWNER CHAS',
    category: 'chassis',
    skipQuickSearch: true
  },
  {
    width: 144,
    field: 'fmcsaDate',
    type: 'date',
    label: 'FMCSA',
    category: 'chassis',
    // sortable: false,
    dateFormat: 'MM/YYYY'
  },
  {
    width: 144,
    field: 'licensePlateExpirationDate',
    type: 'date',
    label: 'LP EXP',
    category: 'chassis',
    // sortable: false,
    dateFormat: 'MM/YYYY'
  },
  { width: 144, field: 'chassisLicensePlate', label: 'CHAS LP', category: 'chassis' },
  { width: 144, field: 'notes', label: 'NOTES', category: 'common' }
];

export const defaultTransactionColumns: Array<keyof TransactionColumnType> = [
  'id',
  'completedDate',
  'type',
  'container',
  'chassis',
  'gensets',
  'sizeType',
  'truckingCompany'
];
